import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { FolderOpen, Loader2, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import useTaskActivity from "@/hooks/useTaskActivity";
import useTaskAutosave from "@/hooks/useTaskAutosave";
import TaskForm from "@/components/task/TaskForm";
import TaskChecklist from "@/components/task/TaskChecklist";
import TaskAttachments from "@/components/task/TaskAttachments";
import TaskTimeTracking from "@/components/task/TaskTimeTracking";
import TaskComments from "@/components/task/TaskComments";
import TaskActivity from "@/components/task/TaskActivity";

const EMPTY = { name: "", description: "", description_mentions: [], assigned_to: [], start_date: "", end_date: "", status: "assigned", priority: "none" };

function SaveStatus({ status, onRetry }) {
  if (status === "saving") return <span className="inline-flex items-center gap-1 text-xs text-zinc-500" data-testid="task-save-status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</span>;
  if (status === "saved") return <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid="task-save-status"><Check className="h-3.5 w-3.5" /> Saved</span>;
  if (status === "error") return <button onClick={onRetry} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline" data-testid="task-save-retry"><AlertCircle className="h-3.5 w-3.5" /> Couldn't save – retry</button>;
  return null;
}

export default function TaskDialog({ open, onOpenChange, task, projectId, projectName, builderName, members, canManage, currentUser, onSaved }) {
  const navigate = useNavigate();
  const isEdit = !!task;
  const isAssignee = task ? (task.assigned_to || []).includes(currentUser.user_id) : false;
  const readOnlyFields = isEdit && !canManage; // members can only change status/assignees

  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [checklist, setChecklist] = useState([]);

  const { activity, actHasMore, loadingOlder, refreshActivity, loadOlderActivity, resetActivity } = useTaskActivity(task);
  const autosave = useTaskAutosave(task?.task_id, onSaved);

  const memberMap = Object.fromEntries(members.map((m) => [m.user_id, m]));
  const doneCount = checklist.filter((c) => c.done).length;

  // Initialise on open (keyed by task_id so onSaved refreshes never clobber in-progress edits).
  useEffect(() => {
    if (open && task) {
      setForm({
        name: task.name, description: task.description || "", description_mentions: task.description_mentions || [],
        assigned_to: task.assigned_to || [],
        start_date: task.start_date || "", end_date: task.end_date || "",
        status: task.status, priority: task.priority || "none",
      });
      setChecklist(task.checklist || []);
      refreshActivity();
    } else if (!open) {
      setForm(EMPTY);
      setChecklist([]);
      resetActivity();
      autosave.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.task_id]);

  // Auto-save (edit) OR just update local form (create).
  const change = (patch, { immediate } = {}) => {
    setForm((f) => ({ ...f, ...patch }));
    if (!isEdit) return;
    if (readOnlyFields) {
      const allowed = {};
      if ("status" in patch) allowed.status = patch.status;
      if ("assigned_to" in patch) allowed.assigned_to = patch.assigned_to;
      if (Object.keys(allowed).length === 0) return;
      autosave.save(allowed);
      return;
    }
    if (immediate) { autosave.flush(); autosave.save(patch); }
    else autosave.scheduleText(patch);
  };

  const handleOpenChange = (o) => {
    if (!o && isEdit) autosave.flush(); // flush pending debounced save before closing
    onOpenChange(o);
  };

  const createTask = async () => {
    if (!form.name.trim()) { toast.error("Task name is required"); return; }
    setCreating(true);
    try {
      await api.post(`/projects/${projectId}/tasks`, form);
      toast.success("Task created");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErr(e, "Failed to create task"));
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-sm p-0 gap-0 max-w-lg sm:max-w-4xl w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Sticky header: task name + folder link + save status + (built-in X) */}
        <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-5 sm:px-6 py-4 pr-12">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="font-heading text-base sm:text-lg truncate">
                {isEdit ? (form.name || "Task Details") : "New Task"}
              </DialogTitle>
              {isEdit && (task?.project_name || projectName) && (
                <button
                  type="button"
                  onClick={() => { handleOpenChange(false); navigate(`/project/${task?.project_id || projectId}`); }}
                  className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 hover:underline w-fit"
                  data-testid="task-dialog-project-link"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  {task?.builder || builderName ? `${task?.builder || builderName} · ` : ""}{task?.project_name || projectName}
                </button>
              )}
            </div>
            {isEdit && <div className="shrink-0 pt-0.5"><SaveStatus status={autosave.status} onRetry={autosave.retry} /></div>}
          </div>
          <DialogDescription className="sr-only">
            {isEdit ? "View and edit this task. Changes save automatically." : "Create a new task for this project."}
          </DialogDescription>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content (left) */}
            <div className="lg:col-span-2 space-y-5 min-w-0">
              <TaskForm section="main"
                form={form} change={change} readOnlyFields={readOnlyFields} members={members}
                checklist={checklist} doneCount={doneCount} isEdit={isEdit} task={task} memberMap={memberMap}
              />
              {isEdit && <TaskComments task={task} members={members} refreshActivity={refreshActivity} />}
              {isEdit && (
                <TaskChecklist
                  task={task} checklist={checklist} setChecklist={setChecklist}
                  canManage={canManage} isAssignee={isAssignee} currentUser={currentUser}
                  members={members} memberMap={memberMap} onSaved={onSaved} refreshActivity={refreshActivity}
                />
              )}
              {isEdit && (
                <TaskAttachments
                  task={task} canManage={canManage} isAssignee={isAssignee}
                  onSaved={onSaved} refreshActivity={refreshActivity}
                />
              )}
              {isEdit && (
                <TaskActivity
                  activity={activity} actHasMore={actHasMore}
                  loadOlderActivity={loadOlderActivity} loadingOlder={loadingOlder}
                  taskId={task?.task_id}
                  onMarkupSaved={() => { refreshActivity(); onSaved(); }}
                />
              )}
            </div>

            {/* Details (right) */}
            <div className="space-y-5 min-w-0 lg:border-l lg:border-zinc-100 lg:pl-6">
              <TaskForm section="details"
                form={form} change={change} readOnlyFields={readOnlyFields} members={members}
                checklist={checklist} doneCount={doneCount} isEdit={isEdit} task={task} memberMap={memberMap}
              />
              {isEdit && (
                <TaskTimeTracking
                  task={task} canManage={canManage} currentUser={currentUser} refreshActivity={refreshActivity}
                />
              )}
            </div>
          </div>
        </div>

        {/* Create-mode footer only — edits auto-save (no button). */}
        {!isEdit && (
          <div className="border-t border-zinc-200 px-5 sm:px-6 py-4 flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="save-task-button" onClick={createTask} disabled={creating} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm">
              {creating ? "Creating…" : "Create Task"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
